import { NavUser } from "@/components/layouts/v2/layout/nav-user";
import useTeamsApp from "@/hooks/useTeamsApp";
import { Plus } from "@cmp-src/icons/TacticIcons/Plus";
import { SidebarSearchAnimated } from "@cmp-src/icons/TacticIcons/SidebarSearchAnimated";
import { TacticIcon } from "@cmp-src/icons/TacticLogo/TacticIcon";
import { OrganizationSettings } from "@gettactic/api";
import { Permissions } from "@gettactic/api";
import { useDisclosure } from "@gettactic/helpers";
import { authenticatedUserIsInMoreOrganizations } from "@gettactic/helpers/src/features/authenticatedUserIsInMoreOrganizations";
import { useBreakpoint } from "@gettactic/helpers/src/hooks/useBreakpoint";
import { isTacticAppWebView } from "@gettactic/helpers/src/isTacticAppWebView";
import { useAuthenticated, useOrgFeatureFlag } from "@gettactic/hooks";
import { OrganizationSwitcher } from "@v2/components/organizations/OrganizationSwitcher/OrganizationSwitcher";
import { Link } from "@v2/components/ui/Link";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger
} from "@v2/components/ui/dropdown-menu";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger
} from "@v2/components/ui/tooltip";
import { cn } from "@v2/lib/utils";
import { useGlobalSearch } from "@webV2/components/global-search";
import { MobileGlobalReserveActionSheet } from "@webV2/components/mobile/MobileGlobalReserveActionSheet";
import { WebRoutes } from "@webV2/defines/routes";
import { useRouterPush } from "@webV2/helpers/router/useRouterPush";
import { ChatBotBubble } from "@webV2/pages/Tessa/bubble/ChatBotBubble";
import { useLayout } from "@webV2/providers/LayoutProvider/context";
import { LayoutMenuItem } from "@webV2/providers/LayoutProvider/types";
import { useReserve } from "@webV2/providers/ReserveProvider/context";
import { MenuIcon } from "lucide-react";
import { useTranslation } from "next-i18next";
import React from "react";
import { ImpersonationButton } from "./ImpersonationButton";
import { getControlKey } from "utils/os-helpers";
import { useAskTessa } from "@v2/components/ai/AskTessaProvider";

export const roundButtonClassNames =
  "flex h-[36px] w-[36px] items-center justify-center rounded-full";
const classes = {
  Wrapper:
    "justify-between w-full fixed left-0 right-0 z-sticky md:static md:h-full py-2 md:py-5 md:overflow-hidden flex md:flex-col",
  Logo: "w-6 md:w-auto hidden md:block text-primary-bg relative",
  SectionsContainer:
    "md:border-t-0 h-20 bg-red-200 md:h-auto items-end md:items-start md:shadow-none fixed py-2 md:py-0 md:bg-transparent flex justify-center md:justify-start md:flex-col md:static z-sticky bottom-0 left-0 right-0 mt-6 gap-6 md:flex-1 overflow-hidden",
  LogoIcon: "size-8.5 cursor-pointer",
  MenuItem:
    "size-9 overflow-hidden p-1.5 transition-colors hover:text-v2-sidebar-icons-selected hover:cursor-pointer",
  MenuItemActive: "text-v2-sidebar-icons-selected",
  MenuItemNotActive: "text-v2-sidebar-icons",
  Icon: "size-6",
  FooterContainer:
    "hidden md:flex flex-row md:flex-col gap-5 md:mt-5 items-center justify-center",
  PlusButton: `${roundButtonClassNames} hidden md:flex bg-primary text-primary-text`,
  MdPlusButton: `${roundButtonClassNames} md:hidden bg-primary text-primary-text`,
  PlusIcon: "size-[18px]",
  ExtraButton: "p-1.5",
  ExtraIcon: "size-6"
};

const isSectionEnabled = (
  enabled:
    | boolean
    | ((permissions: Permissions) => boolean)
    | LayoutMenuItem["enabled"],
  permissions: Permissions,
  featureFlags?: NonNullable<OrganizationSettings["feature_flags"]>
): boolean => {
  if (typeof enabled === "function") {
    // Check if it's a feature flags function (1 parameter)
    if (enabled.length === 1 && featureFlags) {
      return (
        enabled as (
          featureFlags: NonNullable<OrganizationSettings["feature_flags"]>
        ) => boolean
      )(featureFlags);
    }
    // Otherwise it's a permissions function
    return (enabled as (permissions: Permissions) => boolean)(permissions);
  }
  return enabled;
};

export const MainSidebar: React.FC = () => {
  const {
    userContext: { authenticatedUser }
  } = useAuthenticated();
  const askTessa = useAskTessa();
  const workplaceRequestsFeatureFlag = useOrgFeatureFlag("workplace_requests");
  const tessaFeatureFlag = useOrgFeatureFlag("tessa");
  const organizationSwitcherDisclosure = useDisclosure();
  const { isBreakpoint } = useBreakpoint();
  const { sidebarSize, navSections, selectedFloor } = useLayout();
  const isMobileApp = isTacticAppWebView();
  const isTeamsApp = !!useTeamsApp();

  const { openReserveModal } = useReserve();
  const routerPush = useRouterPush();
  const { t } = useTranslation("common");
  const mobileReserveSheetDisclosure = useDisclosure();
  const globalSearch = useGlobalSearch();

  const isMd = isBreakpoint("md");

  const handleReserveRoom = (e: React.MouseEvent) => {
    // we gotta wait for the dropdown to close before opening the modal
    // otherwise the modal will open and close immediately

    openReserveModal({
      resourceType: "meeting_room"
    });
  };

  const handleReserveDesk = (e: React.MouseEvent) => {
    // we gotta wait for the dropdown to close before opening the modal
    // otherwise the modal will open and close immediately

    openReserveModal({
      resourceType: "desk"
    });
  };

  const inMoreOrganizations =
    authenticatedUserIsInMoreOrganizations(authenticatedUser);

  // TODO: this this feature flag object mess
  const filterFeatureFlags: Record<
    string,
    NonNullable<OrganizationSettings["feature_flags"]>
  > = {
    "workplace-requests": {
      workplace_requests: workplaceRequestsFeatureFlag ?? false,
      tessa: tessaFeatureFlag ?? false
    },
    tessa: {
      workplace_requests: workplaceRequestsFeatureFlag ?? false,
      tessa: tessaFeatureFlag ?? false
    }
  };

  const visibleNavSections = React.useMemo(() => {
    const sections = navSections.filter(
      (section) =>
        (isMd || !section.extraOnMobile) &&
        isSectionEnabled(
          section.enabled,
          authenticatedUser.permissions,
          filterFeatureFlags[section.id]
        )
    );
    if (!isMd) {
      sections.push({
        id: "more",
        title: "More",
        enabled: true,
        icon: () => <MenuIcon className={"size-6"} />,
        url: "#",
        active: false,
        appendFloorId: false,
        extraOnMobile: false,
        isMoreButton: true
      });
    }
    return sections;
  }, [
    navSections,
    isMd,
    authenticatedUser.permissions,
    workplaceRequestsFeatureFlag
  ]);
  const isDesktop = isBreakpoint("md");
  const getGlobalSearchButton = (type: "desktop" | "mobile" = "desktop") => {
    if (!isDesktop) {
      return null;
    }
    const button = (
      <Tooltip>
        <TooltipTrigger asChild>
          <button
            className={cn(
              type === "mobile" ? classes.MdPlusButton : classes.PlusButton,
              "ml-[3px] bg-transparent text-v2-sidebar-icons transition-2 transition-all"
            )}
            onClick={() => globalSearch.open("")}
          >
            <SidebarSearchAnimated />
          </button>
        </TooltipTrigger>
        <TooltipContent side={isMd ? "right" : "top"}>
          <span>
            Search -{" "}
            <span className={"text-neutral-600 text-sm"}>
              {getControlKey()}K
            </span>
          </span>
        </TooltipContent>
      </Tooltip>
    );

    return button;
  };
  const getReservationButton = (type: "desktop" | "mobile" = "desktop") => {
    if (type === "desktop") {
      return null;
    }
    const button = (
      <>
        <button
          className={
            type === "mobile" ? classes.MdPlusButton : classes.PlusButton
          }
          onClick={
            type === "mobile"
              ? () => {
                  mobileReserveSheetDisclosure.open();
                }
              : undefined
          }
        >
          <Plus className={classes.PlusIcon} />
        </button>
      </>
    );

    if (type === "mobile") {
      return button;
    }

    return (
      <DropdownMenu>
        <DropdownMenuTrigger>{button}</DropdownMenuTrigger>
        <DropdownMenuContent
          align={isMd ? "end" : "center"}
          side={isMd ? "right" : "top"}
        >
          <DropdownMenuItem onClick={handleReserveRoom}>
            {t("navActions.reserveRoom")}
          </DropdownMenuItem>
          <DropdownMenuItem onClick={handleReserveDesk}>
            {t("navActions.reserveDesk")}
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    );
  };

  return (
    <TooltipProvider delayDuration={0}>
      <div
        className={cn(classes.Wrapper, isTeamsApp ? "px-5" : "px-4")}
        style={
          isMd
            ? { width: sidebarSize }
            : {
                background:
                  "linear-gradient(to bottom, #F7F9FB 50%,  rgba(247, 249, 251, 0) 100%)"
              }
        }
      >
        <Link
          onClick={(event) => {
            event.preventDefault();
            event.stopPropagation();
            if (inMoreOrganizations) {
              organizationSwitcherDisclosure.open();
              return;
            }
            routerPush(WebRoutes.dashboard);
          }}
          href={WebRoutes.dashboard}
          className={classes.Logo}
        >
          <span>
            <TacticIcon className={classes.LogoIcon} />
          </span>
        </Link>
        <div
          className={cn(
            classes.SectionsContainer,
            isMobileApp ? "h-28 pb-6" : undefined
          )}
          style={
            isMd
              ? undefined
              : {
                  background:
                    "linear-gradient(to top, #F7F9FB 50%,  rgba(247, 249, 251, 0) 100%)"
                }
          }
        >
          {visibleNavSections.map((section, index) => {
            const menuContent = (
              <div
                className={cn(
                  classes.MenuItem,
                  section.active
                    ? classes.MenuItemActive
                    : classes.MenuItemNotActive
                )}
                key={section.id}
              >
                <Tooltip>
                  <TooltipTrigger>
                    {React.createElement(section.icon, {
                      className: classes.Icon,
                      isActive: section.active
                    })}
                  </TooltipTrigger>
                  <TooltipContent side={isMd ? "right" : "top"} align="center">
                    {t(`navItems.${section.id}`)}
                  </TooltipContent>
                </Tooltip>
              </div>
            );
            return (
              <>
                {index === Math.floor(visibleNavSections.length / 2) && (
                  <>
                    {getReservationButton("mobile")}
                    <MobileGlobalReserveActionSheet
                      isOpen={mobileReserveSheetDisclosure.isOpen}
                      onOpenChange={mobileReserveSheetDisclosure.onOpenChange}
                    />
                  </>
                )}

                {section.isMoreButton ? (
                  <NavUser
                    key={section.id}
                    user={{
                      name: authenticatedUser.user?.name || "",
                      email: authenticatedUser.user?.email || "",
                      avatar: authenticatedUser.user?.profile_photo_url || ""
                    }}
                    trigger={menuContent}
                  />
                ) : (
                  <Link
                    key={section.id}
                    href={
                      section.url +
                      (section.appendFloorId && selectedFloor
                        ? `?floorId=${selectedFloor.id}`
                        : "")
                    }
                    onAfterClick={askTessa?.tessaDisclosure?.close}
                  >
                    {menuContent}
                  </Link>
                )}
              </>
            );
          })}
        </div>
        <div className={classes.FooterContainer}>
          {getGlobalSearchButton("desktop")}
          {tessaFeatureFlag && (
            <ChatBotBubble className={roundButtonClassNames} />
          )}
          <ImpersonationButton />
          {getReservationButton("desktop")}
          <NavUser
            user={{
              name: authenticatedUser.user?.name || "",
              email: authenticatedUser.user?.email || "",
              avatar: authenticatedUser.user?.profile_photo_url || ""
            }}
          />
        </div>
      </div>
      {inMoreOrganizations && (
        <OrganizationSwitcher
          isOpen={organizationSwitcherDisclosure.isOpen}
          onOpenChange={organizationSwitcherDisclosure.onOpenChange}
        />
      )}
    </TooltipProvider>
  );
};
